import { useState, useEffect } from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
// Note: framer-motion still used for mobile sidebar animations
import { Toaster } from 'react-hot-toast';
import ErrorBoundary from '@/components/common/ErrorBoundary';
import { FullPageLoader } from '@/components/common';
import Sidebar from './Sidebar';
import Header from './Header';
import Breadcrumbs from './Breadcrumbs';
import { useAuth, useAuthActions } from '@/stores/auth';
import { BreadcrumbProvider } from '@/contexts/BreadcrumbContext';
import { InactivityProvider } from '@/components/providers/InactivityProvider';
import { isPathEnabled } from '@/config/features';

export default function AppLayout() {
  const { isAuthenticated, isLoading } = useAuth();
  const { loadUserFromStorage } = useAuthActions();
  const location = useLocation();

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);

  // Check if user is authenticated on mount
  // Only load from storage if not already authenticated to avoid race conditions
  useEffect(() => {
    if (!isAuthenticated) {
      loadUserFromStorage();
    }
  }, [isAuthenticated, loadUserFromStorage]);

  // Handle responsive behavior
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      const wasDesktop = !isMobile;

      setIsMobile(mobile);

      if (mobile) {
        // Mobile: always hide the desktop sidebar and close mobile sidebar
        setSidebarCollapsed(true);
        setShowMobileSidebar(false);
      } else if (wasDesktop !== !mobile) {
        // Just switched to desktop: restore previous desktop sidebar state or default to expanded
        const savedCollapsed = localStorage.getItem('sidebarCollapsed');
        setSidebarCollapsed(savedCollapsed === 'true' ? true : false);
        setShowMobileSidebar(false);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobile]);

  // Close mobile sidebar when route changes
  useEffect(() => {
    setShowMobileSidebar(false);
  }, [location.pathname]);

  // Close mobile sidebar when clicking backdrop
  const handleBackdropClick = () => {
    setShowMobileSidebar(false);
  };

  const handleSidebarToggle = () => {
    if (isMobile) {
      setShowMobileSidebar(!showMobileSidebar);
    } else {
      const newCollapsed = !sidebarCollapsed;
      setSidebarCollapsed(newCollapsed);
      localStorage.setItem('sidebarCollapsed', newCollapsed.toString());
    }
  };

  // Show loading state
  if (isLoading) {
    return <FullPageLoader text="Loading..." />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }


  return (
    <BreadcrumbProvider>
    <InactivityProvider>
    <div className="min-h-screen bg-secondary-50 dark:bg-secondary-900 flex">
      {/* Mobile sidebar backdrop */}
      <AnimatePresence>
        {isMobile && showMobileSidebar && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleBackdropClick}
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          />
        )}
      </AnimatePresence>

      {/* Desktop sidebar */}
      {!isMobile && (
        <Sidebar
          collapsed={sidebarCollapsed}
          onCollapsedChange={setSidebarCollapsed}
          className="hidden lg:flex"
        />
      )}

      {/* Mobile sidebar */}
      <AnimatePresence>
        {isMobile && showMobileSidebar && (
          <motion.div
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
            className="fixed inset-y-0 left-0 z-50 w-64 lg:hidden"
          >
            <Sidebar
              collapsed={false}
              onCollapsedChange={() => setShowMobileSidebar(false)}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main content area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <Header
          onMenuClick={handleSidebarToggle}
          showMenuButton={isMobile}
        />

        {/* Page content */}
        <main className="flex-1 overflow-y-auto">
          <div>
            {/* Breadcrumbs */}
            <div className="px-4 sm:px-6 py-4 border-b border-secondary-200 dark:border-secondary-700 bg-white dark:bg-secondary-900">
              <Breadcrumbs />
            </div>

            <ErrorBoundary resetKeys={[location.pathname]}>
              {isPathEnabled(location.pathname) ? (
                <Outlet />
              ) : (
                <Navigate to="/security/dashboard" replace />
              )}
            </ErrorBoundary>
          </div>
        </main>
      </div>

      {/* Toast notifications */}
      <Toaster
        position="top-right"
        gutter={8}
        containerClassName="mt-16"
        toastOptions={{
          duration: 4000,
          style: {
            background: 'var(--toast-bg)',
            color: 'var(--toast-color)',
            border: '1px solid var(--toast-border)',
          },
          success: {
            iconTheme: {
              primary: '#22c55e',
              secondary: '#ffffff',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#ffffff',
            },
          },
        }}
      />

      {/* Global styles for toast theming */}
      <style>{`
        :root {
          --toast-bg: white;
          --toast-color: #1e293b;
          --toast-border: #e2e8f0;
        }

        .dark {
          --toast-bg: #1e293b;
          --toast-color: #f1f5f9;
          --toast-border: #475569;
        }
      `}</style>
    </div>
    </InactivityProvider>
    </BreadcrumbProvider>
  );
}